/**
 * Map configuration - all game-specific constants are configurable here.
 *
 * ═══════════════════════════════════════════════════════════════════════
 *  Bitcraft World Geometry
 * ═══════════════════════════════════════════════════════════════════════
 *
 *  The game world is a 5×5 region grid. The accessible area is the
 *  center 3×3 (regions 7-9, 12-14, 17-19) plus four "plus-arm"
 *  regions (3, 11, 15, 23). Regions are numbered like a numpad
 *  starting with 1 in the bottom-left:
 *
 *    Full 5×5:              Accessible plus:
 *    21 22 23 24 25            .  . 23  .  .
 *    16 17 18 19 20            . 17 18 19  .
 *    11 12 13 14 15           11 12 13 14 15
 *     6  7  8  9 10            .  7  8  9  .
 *     1  2  3  4  5            .  .  3  .  .
 *
 *  Each region = 80×80 chunks
 *  Each chunk  = 32×32 tiles (1024 tiles, stored as flat arrays)
 *
 *  Accessible / full 5×5 area = 400×400 chunks = 12,800×12,800 tiles.
 *  Inaccessible cells render as empty (void) — tiles for them are
 *  simply absent on the CDN and the map shows the background color.
 *
 *  Chunk coordinates are global across the full 5×5 grid:
 *    Region 3  (bot-mid arm):  chunkX 160-239, chunkZ   0-79
 *    Region 7  (bot-left):     chunkX  80-159, chunkZ  80-159
 *    Region 8  (bot-mid):      chunkX 160-239, chunkZ  80-159
 *    Region 9  (bot-right):    chunkX 240-319, chunkZ  80-159
 *    Region 11 (mid-left arm): chunkX   0-79,  chunkZ 160-239
 *    Region 12 (mid-left):     chunkX  80-159, chunkZ 160-239
 *    Region 13 (center):       chunkX 160-239, chunkZ 160-239
 *    Region 14 (mid-right):    chunkX 240-319, chunkZ 160-239
 *    Region 15 (mid-right arm):chunkX 320-399, chunkZ 160-239
 *    Region 17 (top-left):     chunkX  80-159, chunkZ 240-319
 *    Region 18 (top-mid):      chunkX 160-239, chunkZ 240-319
 *    Region 19 (top-right):    chunkX 240-319, chunkZ 240-319
 *    Region 23 (top-mid arm):  chunkX 160-239, chunkZ 320-399
 *
 *  The accessible area origin is at global chunk (0, 0).
 *
 * ═══════════════════════════════════════════════════════════════════════
 *  Per-Tile Data (TerrainChunkState)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *  Each chunk has these data arrays of length 1024 (32×32 tiles):
 *
 *  Biomes            - packed uint32. Byte 0 = primary biome ID,
 *                      higher bytes = secondary biome layers for
 *                      blending at boundaries.
 *
 *  BiomeDensity      - blend weight for biome layers.
 *                      128 = default (single-biome tiles).
 *
 *  Elevations        - height in game units. Range: -23 to 537.
 *
 *  OriginalElevations - pre-modification elevation (before player edits).
 *
 *  WaterLevels       - water surface height. 0 = dry land.
 *
 *  WaterBodyTypes    - 0 = none, 1 = river, 2 = lake, 3 = coastal,
 *                      4 = ocean, 5 = deep water
 *
 *  ZoningTypes       - 0 = natural terrain, 1 = player-claimed zone.
 *
 *  Dimension         - 1 = surface (the only layer with terrain data).
 *
 * ═══════════════════════════════════════════════════════════════════════
 *  Biome IDs (byte 0 of the packed biome value, from BiomeDesc.json)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   0  = Dev              - void/unloaded
 *   1  = Calm Forest      - 22.5%, most common land biome
 *   2  = Pine Woods       - 13.0%
 *   3  = Snowy Peaks      -  1.1%, "Freezing Cold" hazard
 *   4  = Breezy Grasslands-  5.6%
 *   5  = Autumn Forest    -  4.7%
 *   6  = Misty Tundra     -  3.6%
 *   7  = Desert Wasteland -  2.8%, "Blazing Heat" hazard
 *   8  = Swamp            -  1.0%
 *   9  = Rocky Garden     - 12.9%
 *   10 = Open Ocean       - 27.3%
 *   11 = Safe Meadows     -  1.9%, spawn area (no player building)
 *   12 = Cave             -  not observed on surface
 *   13 = Jungle           -  2.3%
 *   14 = Sapwoods         -  1.5%, no player building
 */

// ── World geometry constants ─────────────────────────────────────────────────

/** Number of regions per side of the full grid. */
export const REGIONS_PER_SIDE = 5;

/** Number of accessible regions per side (now the full 5×5 grid). */
export const ACCESSIBLE_REGIONS_PER_SIDE = 5;

/** Chunks per region side. */
export const CHUNKS_PER_REGION = 80;

/** Tiles per chunk side. */
export const TILES_PER_CHUNK = 32;

/** Total tiles per chunk (flat array length). */
export const TILES_PER_CHUNK_AREA = TILES_PER_CHUNK * TILES_PER_CHUNK; // 1024

/** Chunks per side of the accessible area. */
export const ACCESSIBLE_CHUNKS =
  ACCESSIBLE_REGIONS_PER_SIDE * CHUNKS_PER_REGION; // 400

/** Total tiles per side of the accessible area. */
export const ACCESSIBLE_TILES = ACCESSIBLE_CHUNKS * TILES_PER_CHUNK; // 12800

/** Global chunk offset where the accessible area begins. */
export const GLOBAL_CHUNK_ORIGIN = 0;

// ── Region mapping ───────────────────────────────────────────────────────────

/**
 * Accessible region IDs mapped to their chunk coordinate offsets.
 *
 * `(col, row)` are positions within the full 5×5 numpad grid (0-4),
 * with col increasing east (game +X) and row increasing north (game +Z).
 *
 * `(chunkOffsetX, chunkOffsetZ)` are chunk positions relative to the
 * accessible-area origin. The global chunk coordinate of a region's
 * lower-left corner is `chunkOffsetX + GLOBAL_CHUNK_ORIGIN`,
 * `chunkOffsetZ + GLOBAL_CHUNK_ORIGIN`.
 *
 * BREAKING CHANGE (vs. the 3×3 layout):
 *   - `(col, row)` are now 0-4 across the full 5×5 grid, not 0-2 inside
 *     the accessible block. Code that lays out a `repeat(3, …)` CSS grid
 *     or divides a UI dimension by 3 must update to 5.
 *   - The accessible-area origin moved from global chunk (80,80) to (0,0)
 *     and the per-region `chunkOffset*` values shifted by +80 to match.
 *     `chunkOffset* + GLOBAL_CHUNK_ORIGIN` still yields the same global
 *     chunk coordinate as before, so global-coord math is unaffected.
 */
export const ACCESSIBLE_REGIONS: Record<
  number,
  { col: number; row: number; chunkOffsetX: number; chunkOffsetZ: number }
> = {
  3: { col: 2, row: 0, chunkOffsetX: 160, chunkOffsetZ: 0 },
  7: { col: 1, row: 1, chunkOffsetX: 80, chunkOffsetZ: 80 },
  8: { col: 2, row: 1, chunkOffsetX: 160, chunkOffsetZ: 80 },
  9: { col: 3, row: 1, chunkOffsetX: 240, chunkOffsetZ: 80 },
  11: { col: 0, row: 2, chunkOffsetX: 0, chunkOffsetZ: 160 },
  12: { col: 1, row: 2, chunkOffsetX: 80, chunkOffsetZ: 160 },
  13: { col: 2, row: 2, chunkOffsetX: 160, chunkOffsetZ: 160 },
  14: { col: 3, row: 2, chunkOffsetX: 240, chunkOffsetZ: 160 },
  15: { col: 4, row: 2, chunkOffsetX: 320, chunkOffsetZ: 160 },
  17: { col: 1, row: 3, chunkOffsetX: 80, chunkOffsetZ: 240 },
  18: { col: 2, row: 3, chunkOffsetX: 160, chunkOffsetZ: 240 },
  19: { col: 3, row: 3, chunkOffsetX: 240, chunkOffsetZ: 240 },
  23: { col: 2, row: 4, chunkOffsetX: 160, chunkOffsetZ: 320 },
};

// ── Water body types ─────────────────────────────────────────────────────────

export const WATER_BODY_TYPES = {
  NONE: 0,
  RIVER: 1,
  LAKE: 2,
  COASTAL: 3,
  OCEAN: 4,
  DEEP: 5,
} as const;

// ── Biome definitions ────────────────────────────────────────────────────────

export interface BiomeDef {
  /** Biome ID (byte 0 of packed biome value). */
  id: number;
  /** Display name (from BiomeDesc.json). */
  name: string;
  /** Base color as 0xRRGGBB. */
  color: number;
  /** CSS hex string version of color. */
  colorHex: string;
}

/**
 * Palette indexed by biome ID.
 * Names from BiomeDesc.json, colors chosen to match biome character.
 */
export const BIOMES: Record<number, BiomeDef> = {
  0: { id: 0, name: "Dev", color: 0x0a0e17, colorHex: "#0a0e17" },
  1: { id: 1, name: "Calm Forest", color: 0x447830, colorHex: "#447830" },
  2: { id: 2, name: "Pine Woods", color: 0x2d502a, colorHex: "#2d502a" },
  3: { id: 3, name: "Snowy Peaks", color: 0xc8d2dc, colorHex: "#c8d2dc" },
  4: { id: 4, name: "Breezy Grasslands", color: 0x6e963e, colorHex: "#6e963e" },
  5: { id: 5, name: "Autumn Forest", color: 0xa06e2d, colorHex: "#a06e2d" },
  6: { id: 6, name: "Misty Tundra", color: 0x8c9ba5, colorHex: "#8c9ba5" },
  7: { id: 7, name: "Desert Wasteland", color: 0xc2a864, colorHex: "#c2a864" },
  8: { id: 8, name: "Swamp", color: 0x3a5234, colorHex: "#3a5234" },
  9: { id: 9, name: "Rocky Garden", color: 0x877864, colorHex: "#877864" },
  10: { id: 10, name: "Open Ocean", color: 0x1a4a7a, colorHex: "#1a4a7a" },
  11: { id: 11, name: "Safe Meadows", color: 0x5f9b46, colorHex: "#5f9b46" },
  12: { id: 12, name: "Cave", color: 0x372d28, colorHex: "#372d28" },
  13: { id: 13, name: "Jungle", color: 0x1e6b2a, colorHex: "#1e6b2a" },
  14: { id: 14, name: "Sapwoods", color: 0x8c6932, colorHex: "#8c6932" },
  15: { id: 15, name: "Deserted Beach", color: 0xdac682, colorHex: "#dac682" },
  16: { id: 16, name: "Tropical Canopy", color: 0x3f8b48, colorHex: "#3f8b48" },
  17: { id: 17, name: "Volcanic Crag", color: 0x191412, colorHex: "#191412" },
  18: { id: 18, name: "Uncharted Ocean", color: 0x1a4a7a, colorHex: "#1a4a7a" },
};

/** Fallback biome for unknown IDs. */
export const BIOME_UNKNOWN: BiomeDef = {
  id: -1,
  name: "Unknown",
  color: 0x3a1a3a,
  colorHex: "#3a1a3a",
};

/** Look up a biome definition by its primary ID. */
export function getBiome(id: number): BiomeDef {
  return BIOMES[id] ?? BIOME_UNKNOWN;
}

// ── Biome helpers ────────────────────────────────────────────────────────────

/** Extract the primary (dominant) biome ID from a packed biome value. */
export function primaryBiome(packedBiome: number): number {
  return packedBiome & 0xff;
}

/** Extract all biome layers from a packed biome value. */
export function biomeLayers(packedBiome: number): number[] {
  const layers: number[] = [];
  let v = packedBiome;
  while (v) {
    const id = v & 0xff;
    if (id) layers.push(id);
    v >>>= 8;
  }
  return layers.length > 0 ? layers : [0];
}

// ── Map renderer config ──────────────────────────────────────────────────────

export interface MapConfig {
  /**
   * Total world size in tiles (assumes square world).
   * For the full 5×5 accessible area this is 12800.
   */
  worldSize: number;

  /** Tile image dimensions in pixels (tiles are square). */
  tilePx: number;

  /** Minimum zoom level (most zoomed out). */
  minZoom: number;

  /** Maximum zoom level (most zoomed in). */
  maxZoom: number;

  /**
   * URL template for fetching tile images.
   * Placeholders: {z}, {x}, {y}
   */
  tileUrl: string;

  /** Maximum number of tiles to keep in the LRU cache. */
  tileCacheSize: number;

  /** Cache-bust string appended as ?v= to tile URLs. Busts browser HTTP cache. */
  tileCacheBust?: string;

  /** Background color for the map canvas (hex number). */
  backgroundColor: number;

  /** Camera friction for inertia (0 = no friction, 1 = instant stop). */
  cameraFriction: number;

  /** Camera zoom easing factor (0-1, higher = faster). */
  cameraZoomEasing: number;

  /** Camera pan easing factor (0-1, higher = faster). */
  cameraPanEasing: number;

  /** Minimum camera scale (most zoomed out). Derived from minZoom if not set. */
  cameraMinScale?: number;

  /** Maximum camera scale (most zoomed in). Derived from maxZoom if not set. */
  cameraMaxScale?: number;

  /** Padding in pixels for bounds clamping. */
  boundsPadding: number;
}

export const DEFAULT_CONFIG: MapConfig = {
  worldSize: ACCESSIBLE_TILES,
  tilePx: 256,
  minZoom: 0,
  maxZoom: 8,
  tileUrl: "/tiles/{z}/{x}/{y}.webp",
  tileCacheSize: 512,
  tileCacheBust: "1",
  backgroundColor: 0x060d18,
  cameraFriction: 0.92,
  cameraZoomEasing: 0.18,
  cameraPanEasing: 0.12,
  boundsPadding: 200,
};

/** Compute camera scale from a zoom level. */
export function zoomToScale(zoom: number, config: MapConfig): number {
  return (config.tilePx * Math.pow(2, zoom)) / config.worldSize;
}

/** Compute zoom level from a camera scale. */
export function scaleToZoom(scale: number, config: MapConfig): number {
  return Math.log2((scale * config.worldSize) / config.tilePx);
}
